import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';

const API_BASE = '/api/admin/';

const ACTION_CONFIG = {
    CREATE: { label: 'Create', color: '#0a6640', bg: '#d1e7dd', border: '#a3cfbb' },
    UPDATE: { label: 'Update', color: '#084298', bg: '#cfe2ff', border: '#9ec5fe' },
    DELETE: { label: 'Delete', color: '#842029', bg: '#f8d7da', border: '#f1aeb5' },
    ACCESS: { label: 'Access', color: '#41464b', bg: '#e2e3e5', border: '#c4c8cb' },
};

const labelStyle = {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#495057',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
};

const inputStyle = {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #ced4da',
    borderRadius: 6,
    fontSize: '0.875rem',
    color: '#212529',
    background: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
};

const thStyle = {
    padding: '10px 14px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#6c757d',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #dee2e6',
};

const tdStyle = {
    padding: '10px 14px',
    verticalAlign: 'middle',
    fontSize: '0.875rem',
};

function ActionBadge({ action }) {
    const cfg = ACTION_CONFIG[action] || ACTION_CONFIG.ACCESS;
    return (
        <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: 20,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: cfg.color,
            background: cfg.bg,
            border: `1px solid ${cfg.border}`,
        }}>
            {cfg.label.toUpperCase()}
        </span>
    );
}

function ChangesTable({ changes }) {
    const entries = Object.entries(changes || {});
    if (!entries.length) return <span style={{ color: '#aaa' }}>No field details</span>;
    return (
        <table style={{ borderCollapse: 'collapse', width: 'max-content', maxWidth: '50%', fontSize: '0.82rem', tableLayout: 'auto' }}>
            <thead>
                <tr>
                    <th style={{ ...thStyle, padding: '6px 10px', background: '#e9ecef', borderRadius: '4px 0 0 0', whiteSpace: 'nowrap' }}>Field</th>
                    <th style={{ ...thStyle, padding: '6px 10px', background: '#e9ecef', color: '#842029', whiteSpace: 'nowrap' }}>Before</th>
                    <th style={{ ...thStyle, padding: '6px 10px', background: '#e9ecef', color: '#0a6640', borderRadius: '0 4px 0 0', whiteSpace: 'nowrap' }}>After</th>
                </tr>
            </thead>
            <tbody>
                {entries.map(([field, values]) => {
                    const [before, after] = Array.isArray(values) ? values : [null, values];
                    return (
                        <tr key={field}>
                            <td style={{ ...tdStyle, padding: '5px 10px', fontWeight: 600, color: '#495057', whiteSpace: 'nowrap' }}>
                                {field.replace(/_/g, ' ')}
                            </td>
                            <td style={{ ...tdStyle, padding: '5px 10px', color: '#842029', fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {before == null || before === '' ? <span style={{ color: '#aaa', fontStyle: 'italic' }}>empty</span> : String(before)}
                            </td>
                            <td style={{ ...tdStyle, padding: '5px 10px', color: '#0a6640', fontFamily: 'monospace', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {after == null || after === '' ? <span style={{ color: '#aaa', fontStyle: 'italic' }}>empty</span> : String(after)}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function PaginationBar({ page, totalPages, onPageChange, loading }) {
    if (totalPages <= 1) return null;

    const maxVisible = 7;
    let pages = [];

    if (totalPages <= maxVisible) {
        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
        const half = Math.floor(maxVisible / 2);
        let start = Math.max(1, page - half);
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
        if (start > 1) pages.push(1, '...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages) pages.push('...', totalPages);
    }

    const btnBase = {
        border: '1px solid #dee2e6',
        background: '#fff',
        borderRadius: 6,
        padding: '5px 10px',
        fontSize: '0.82rem',
        cursor: 'pointer',
        minWidth: 34,
        fontWeight: 500,
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button style={{ ...btnBase, color: page === 1 ? '#aaa' : '#0d6efd' }}
                disabled={page === 1 || loading} onClick={() => onPageChange(page - 1)}>
                ‹ Prev
            </button>
            {pages.map((p, i) =>
                p === '...' ? (
                    <span key={`ellipsis-${i}`} style={{ padding: '5px 4px', color: '#aaa', fontSize: '0.82rem' }}>…</span>
                ) : (
                    <button key={p}
                        style={{ ...btnBase, background: p === page ? '#0d6efd' : '#fff', color: p === page ? '#fff' : '#212529', borderColor: p === page ? '#0d6efd' : '#dee2e6', fontWeight: p === page ? 700 : 500 }}
                        disabled={loading} onClick={() => onPageChange(p)}>
                        {p}
                    </button>
                )
            )}
            <button style={{ ...btnBase, color: page === totalPages ? '#aaa' : '#0d6efd' }}
                disabled={page === totalPages || loading} onClick={() => onPageChange(page + 1)}>
                Next ›
            </button>
        </div>
    );
}

function formatTimestamp(iso) {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

const PAGE_SIZE = 50;

export default function UserLogs() {
    const [filters, setFilters] = useState({ user: '', module: '', action: '', date_from: '', date_to: '', record: '' });
    const [results, setResults]   = useState([]);
    const [count, setCount]       = useState(0);
    const [page, setPage]         = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading]   = useState(false);
    const [searched, setSearched] = useState(false);

    const [userOptions, setUserOptions]     = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);

    const [expandedRows, setExpandedRows] = useState(new Set());

    // Load filter options once on mount
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE}user-logs/filters/`, { headers: getAuthHeaders() });
                if (!res.ok) return;
                const data = await res.json();
                setUserOptions(data.users || []);
                setModuleOptions(data.modules || []);
            } catch {
                // non-fatal
            }
        })();
    }, []);

    const fetchLogs = useCallback(async (pageNum) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: pageNum, page_size: PAGE_SIZE });
            if (filters.user)      params.set('user', filters.user);
            if (filters.module)    params.set('module', filters.module);
            if (filters.action !== '') params.set('action', filters.action);
            if (filters.date_from) params.set('date_from', filters.date_from);
            if (filters.date_to)   params.set('date_to', filters.date_to);
            if (filters.record)    params.set('record', filters.record);

            const res = await fetch(`${API_BASE}user-logs/?${params}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch logs');
            const data = await res.json();
            setResults(data.results || []);
            setCount(data.count || 0);
            setPage(data.page || 1);
            setTotalPages(data.total_pages || 0);
            setSearched(true);
        } catch (err) {
            toast.error(err.message || 'Failed to load audit logs');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const handleSearch = () => {
        setExpandedRows(new Set());
        fetchLogs(1);
    };

    const handleReset = () => {
        setFilters({ user: '', module: '', action: '', date_from: '', date_to: '', record: '' });
        setResults([]);
        setCount(0);
        setTotalPages(0);
        setSearched(false);
        setExpandedRows(new Set());
    };

    const handlePageChange = (newPage) => {
        setExpandedRows(new Set());
        fetchLogs(newPage);
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd   = Math.min(page * PAGE_SIZE, count);

    return (
        <div style={{ padding: '24px 28px', fontFamily: 'inherit', maxWidth: '100%' }}>
            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: 'linear-gradient(135deg, #0d6efd 0%, #084298 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <i className="fa-solid fa-clipboard-list" style={{ color: '#fff', fontSize: 18 }} />
                </div>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700, color: '#1a202c', fontSize: '1.2rem' }}>User Activity Logs</h4>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#6c757d' }}>Track who did what, when, and on which record</p>
                </div>
            </div>

            {/* Filter card */}
            <div style={{
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: 12,
                padding: '20px 24px',
                marginBottom: 24,
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
            }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: '14px 20px' }}
                    onKeyDown={handleKeyDown}>
                    <div>
                        <label style={labelStyle}>User</label>
                        <Select2Dropdown
                            options={[{ id: '', label: 'All Users' }, ...userOptions]}
                            value={filters.user}
                            onChange={val => setFilter('user', val)}
                            displayKey="label"
                            valueKey="id"
                            placeholder="All Users"
                            menuPlacement="auto"
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Module</label>
                        <Select2Dropdown
                            options={[{ value: '', label: 'All Modules' }, ...moduleOptions]}
                            value={filters.module}
                            onChange={val => setFilter('module', val)}
                            displayKey="label"
                            valueKey="value"
                            placeholder="All Modules"
                            menuPlacement="auto"
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Action</label>
                        <Select2Dropdown
                            options={[
                                { value: '', label: 'All Actions' },
                                { value: '0', label: 'Create' },
                                { value: '1', label: 'Update' },
                                { value: '2', label: 'Delete' },
                            ]}
                            value={filters.action}
                            onChange={val => setFilter('action', val)}
                            displayKey="label"
                            valueKey="value"
                            placeholder="All Actions"
                            searchable={false}
                            menuPlacement="auto"
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Record</label>
                        <input type="text" style={inputStyle} placeholder="Search record…" value={filters.record} onChange={e => setFilter('record', e.target.value)} />
                    </div>
                    <div>
                        <label style={labelStyle}>Date From</label>
                        <input type="date" style={inputStyle} value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
                    </div>
                    <div>
                        <label style={labelStyle}>Date To</label>
                        <input type="date" style={inputStyle} value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
                    </div>
                </div>

                <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={handleSearch}
                        disabled={loading}
                        style={{
                            padding: '8px 20px',
                            background: loading ? '#6c9ad4' : '#0d6efd',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 7,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                        {loading
                            ? <><i className="fa-solid fa-spinner fa-spin" /> Searching…</>
                            : <><i className="fa-solid fa-magnifying-glass" /> Search</>}
                    </button>
                    <button
                        onClick={handleReset}
                        disabled={loading}
                        style={{
                            padding: '8px 18px',
                            background: '#fff',
                            color: '#6c757d',
                            border: '1px solid #ced4da',
                            borderRadius: 7,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                        <i className="fa-solid fa-rotate-left" /> Reset
                    </button>
                </div>
            </div>

            {/* Empty prompt before first search */}
            {!searched && !loading && (
                <div style={{
                    textAlign: 'center', padding: '80px 0', color: '#adb5bd',
                    background: '#fff', border: '1px dashed #dee2e6',
                    borderRadius: 12,
                }}>
                    <i className="fa-solid fa-filter" style={{ fontSize: 44, marginBottom: 14, display: 'block', color: '#dee2e6' }} />
                    <div style={{ fontSize: '1rem', color: '#6c757d', marginBottom: 6 }}>
                        Set filters and click <strong>Search</strong> to view logs
                    </div>
                    <div style={{ fontSize: '0.82rem' }}>Leave filters blank to load all recent activity.</div>
                </div>
            )}

            {/* Results area */}
            {searched && (
                <>
                    {/* Results header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                            {count === 0
                                ? 'No records found'
                                : `Showing ${rangeStart}–${rangeEnd} of ${count.toLocaleString()} records`}
                        </span>
                        <PaginationBar page={page} totalPages={totalPages} onPageChange={handlePageChange} loading={loading} />
                    </div>

                    {count === 0 ? (
                        <div style={{
                            textAlign: 'center', padding: '60px 0',
                            background: '#fff', border: '1px solid #dee2e6',
                            borderRadius: 12, color: '#adb5bd',
                        }}>
                            <i className="fa-solid fa-inbox" style={{ fontSize: 40, marginBottom: 12, display: 'block' }} />
                            No audit log entries match the selected filters.
                        </div>
                    ) : (
                        <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: 860 }}>
                                    <thead>
                                        <tr style={{ background: '#f8f9fa' }}>
                                            <th style={{ ...thStyle, width: 48 }}>#</th>
                                            <th style={{ ...thStyle, minWidth: 160 }}>Timestamp</th>
                                            <th style={{ ...thStyle, minWidth: 130 }}>User</th>
                                            <th style={{ ...thStyle, minWidth: 130 }}>Module</th>
                                            <th style={{ ...thStyle, minWidth: 180 }}>Record</th>
                                            <th style={{ ...thStyle, width: 90 }}>Action</th>
                                            <th style={{ ...thStyle, minWidth: 110 }}>Changes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((entry, idx) => {
                                            const isExpanded = expandedRows.has(entry.id);
                                            const changeCount = Object.keys(entry.changes || {}).length;
                                            const isEven = idx % 2 === 0;
                                            return (
                                                <React.Fragment key={entry.id}>
                                                    <tr
                                                        style={{
                                                            borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0',
                                                            background: isExpanded ? '#f0f4ff' : (isEven ? '#fff' : '#fafafa'),
                                                            transition: 'background 0.15s',
                                                        }}
                                                    >
                                                        <td style={{ ...tdStyle, color: '#adb5bd', fontSize: '0.78rem' }}>
                                                            {rangeStart + idx}
                                                        </td>
                                                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#555', fontSize: '0.82rem' }}>
                                                            <i className="fa-regular fa-clock" style={{ marginRight: 5, color: '#adb5bd' }} />
                                                            {formatTimestamp(entry.timestamp)}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <div style={{
                                                                    width: 28, height: 28, borderRadius: '50%',
                                                                    background: 'linear-gradient(135deg, #0d6efd, #6610f2)',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    fontSize: '0.7rem', color: '#fff', fontWeight: 700, flexShrink: 0,
                                                                }}>
                                                                    {entry.user.charAt(0).toUpperCase()}
                                                                </div>
                                                                <span style={{ fontWeight: 600, color: '#2c3e50' }}>{entry.user}</span>
                                                            </div>
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <span style={{
                                                                background: '#e9ecef', borderRadius: 5,
                                                                padding: '3px 9px', fontSize: '0.78rem',
                                                                fontFamily: 'monospace', color: '#495057', fontWeight: 600,
                                                            }}>
                                                                {entry.module_display}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...tdStyle, maxWidth: 220 }}>
                                                            <span
                                                                title={entry.record}
                                                                style={{
                                                                    display: 'block',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    color: '#555',
                                                                }}>
                                                                {entry.record}
                                                            </span>
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <ActionBadge action={entry.action} />
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {entry.action === 'UPDATE' && changeCount > 0 ? (
                                                                <button
                                                                    onClick={() => toggleRow(entry.id)}
                                                                    style={{
                                                                        background: isExpanded ? '#e7f1ff' : '#f8f9fa',
                                                                        border: `1px solid ${isExpanded ? '#9ec5fe' : '#ced4da'}`,
                                                                        borderRadius: 6,
                                                                        padding: '4px 10px',
                                                                        fontSize: '0.78rem',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        color: isExpanded ? '#0d6efd' : '#495057',
                                                                        display: 'flex', alignItems: 'center', gap: 5,
                                                                        whiteSpace: 'nowrap',
                                                                    }}>
                                                                    <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ fontSize: '0.7rem' }} />
                                                                    {changeCount} field{changeCount !== 1 ? 's' : ''}
                                                                </button>
                                                            ) : (
                                                                <span style={{ color: '#ced4da', fontSize: '0.85rem' }}>—</span>
                                                            )}
                                                        </td>
                                                    </tr>

                                                    {isExpanded && (
                                                        <tr style={{ background: '#f0f4ff', borderBottom: '2px solid #9ec5fe' }}>
                                                            <td colSpan={7} style={{ padding: '12px 20px 16px 60px' }}>
                                                                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0d6efd', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                                    <i className="fa-solid fa-code-compare" style={{ marginRight: 6 }} />
                                                                    Field Changes
                                                                </div>
                                                                <ChangesTable changes={entry.changes} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Bottom pagination */}
                    {totalPages > 1 && (
                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                            <PaginationBar page={page} totalPages={totalPages} onPageChange={handlePageChange} loading={loading} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
